// Sigil brand mark + lockup + wordmark. See docs/sigil-brand/README.md.

import type { CSSProperties } from 'react';

type Variant = 'mark' | 'lockup' | 'wordmark';
type Color = 'black' | 'blue' | 'white' | 'current';

const HEX: Record<Color, string> = {
  black: '#0A0A0F',
  blue: '#1E50E0',
  white: '#FAFAFA',
  current: 'currentColor',
};

type LogoProps = {
  variant?: Variant;
  color?: Color;
  size?: number;
  className?: string;
  showBlueLine?: boolean;
};

export function Logo({
  variant = 'lockup',
  color = 'black',
  size = 28,
  className = '',
  showBlueLine,
}: LogoProps) {
  const fill = HEX[color];

  if (variant === 'mark') {
    return <Mark fill={fill} size={size} className={className} />;
  }
  if (variant === 'wordmark') {
    return <Wordmark fill={fill} size={size} className={className} showLine={showBlueLine ?? true} />;
  }
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <Mark fill={fill} size={size} />
      <Wordmark fill={fill} size={Math.round(size * 0.62)} showLine={showBlueLine ?? true} />
    </span>
  );
}

function Mark({ fill, size, className = '' }: { fill: string; size: number; className?: string }) {
  const tiny = size <= 24;
  const outerStroke = tiny ? 6 : 4;
  const innerStroke = tiny ? 2.5 : 1.5;
  const sideDot = tiny ? 9 : 7.5;
  const rightDot = tiny ? 7 : 6;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <circle cx="50" cy="50" r="47" stroke={fill} strokeWidth={outerStroke} />
      <circle cx="50" cy="50" r="24" stroke={fill} strokeWidth={innerStroke} />
      <circle cx="38" cy="29.2" r={sideDot} fill={fill} />
      <circle cx="38" cy="70.8" r={sideDot} fill={fill} />
      <circle cx="74" cy="50" r={rightDot} fill={fill} />
    </svg>
  );
}

function Wordmark({
  fill,
  size,
  showLine,
  className = '',
}: {
  fill: string;
  size: number;
  showLine: boolean;
  className?: string;
}) {
  const wordmarkStyle: CSSProperties = {
    fontSize: size,
    color: fill,
    lineHeight: 1,
    fontWeight: 600,
    letterSpacing: '-0.02em',
  };
  return (
    <span className={`inline-flex flex-col items-start gap-1 ${className}`}>
      <span style={wordmarkStyle}>sigil</span>
      {showLine && <span className="h-0.5 w-9 bg-bit2me" />}
    </span>
  );
}
